import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Clock, Building2, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { validateInvite, getLoginUrlWithInvite, type ValidateInviteResponse } from '@/services/inviteApi';

// Provider icons
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GitHubIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

interface ProviderInfo {
  providers: string[];
  dev_mode: boolean;
}

export function InvitePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [inviteData, setInviteData] = useState<ValidateInviteResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [providers, setProviders] = useState<ProviderInfo | null>(null);

  // Store invite code in sessionStorage for OAuth flow
  useEffect(() => {
    if (code) {
      sessionStorage.setItem('invite_code', code);
    }
  }, [code]);

  // Fetch invite details
  useEffect(() => {
    async function fetchInvite() {
      if (!code) {
        setInviteData({ valid: false, error: 'No invite code provided' });
        setIsLoading(false);
        return;
      }

      try {
        const data = await validateInvite(code);
        setInviteData(data);
      } catch (err) {
        console.error('Failed to validate invite:', err);
        setInviteData({ valid: false, error: 'Failed to validate invite' });
      } finally {
        setIsLoading(false);
      }
    }
    fetchInvite();
  }, [code]);

  // Fetch available providers
  useEffect(() => {
    async function fetchProviders() {
      try {
        const response = await fetch('/api/auth/providers');
        if (response.ok) {
          const data = await response.json();
          setProviders(data);
        }
      } catch (err) {
        console.error('Failed to fetch providers:', err);
      }
    }
    fetchProviders();
  }, []);

  // If authenticated, redirect to onboarding
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/onboarding');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Handle OAuth login with invite code
  const handleLogin = (provider: string) => {
    if (code) {
      window.location.href = getLoginUrlWithInvite(provider, code);
    }
  };

  // Loading state
  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid or expired invite
  if (!inviteData?.valid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-2xl">
              {inviteData?.error === 'Invite has expired' ? 'Invite Expired' : 'Invalid Invite'}
            </CardTitle>
            <CardDescription>
              {inviteData?.error === 'Invite has expired'
                ? 'This invitation link has expired. Please contact the person who invited you to request a new invite.'
                : 'This invitation link is not valid. Please check that you have the correct link.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid site invite
  if (inviteData.invite_type === 'site') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <CardTitle className="text-2xl">You've Been Invited!</CardTitle>
            <CardDescription className="space-y-2">
              <p>Create your account and set up your organization.</p>
              {inviteData.expires_at && (
                <p className="text-xs flex items-center justify-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Expires {new Date(inviteData.expires_at).toLocaleDateString()}
                </p>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {inviteData.email && (
              <Alert>
                <AlertDescription className="text-sm">
                  This invite is for <strong>{inviteData.email}</strong>.
                  Please sign in with this email address.
                </AlertDescription>
              </Alert>
            )}

            {providers?.providers.includes('google') && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleLogin('google')}
              >
                <GoogleIcon />
                <span className="ml-2">Continue with Google</span>
              </Button>
            )}

            {providers?.providers.includes('github') && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleLogin('github')}
              >
                <GitHubIcon />
                <span className="ml-2">Continue with GitHub</span>
              </Button>
            )}

            <p className="text-center text-xs text-muted-foreground">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="underline hover:text-foreground"
              >
                Log in
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid org invite
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Building2 className="h-12 w-12 text-blue-500" />
          </div>
          <CardTitle className="text-2xl">
            Join {inviteData.organization_name}
          </CardTitle>
          <CardDescription className="space-y-2">
            {inviteData.team_name ? (
              <p className="flex items-center justify-center gap-2">
                <Users className="h-4 w-4" />
                Team: {inviteData.team_name}
              </p>
            ) : (
              <p>You've been invited to join this organization.</p>
            )}
            {inviteData.expires_at && (
              <p className="text-xs flex items-center justify-center gap-1 text-muted-foreground">
                <Clock className="h-3 w-3" />
                Expires {new Date(inviteData.expires_at).toLocaleDateString()}
              </p>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {inviteData.email && (
            <Alert>
              <AlertDescription className="text-sm">
                This invite is for <strong>{inviteData.email}</strong>.
                Please sign in with this email address.
              </AlertDescription>
            </Alert>
          )}

          {providers?.providers.includes('google') && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleLogin('google')}
            >
              <GoogleIcon />
              <span className="ml-2">Continue with Google</span>
            </Button>
          )}

          {providers?.providers.includes('github') && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleLogin('github')}
            >
              <GitHubIcon />
              <span className="ml-2">Continue with GitHub</span>
            </Button>
          )}

          <p className="text-center text-xs text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="underline hover:text-foreground"
            >
              Log in
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default InvitePage;
